import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type DriverTip, type DriverTipStats } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, StatTile, type Column } from '@/components/ui';
import { IconReceipt } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

/** Compact AUD formatter ($ + 2dp). */
function aud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

/** DriverTips — the read-only audit log of tips riders send drivers (a rider
 *  tip debits the rider's km-wallet and credits the driver's AUD wallet, both
 *  via wallet-svc; earnings-svc records ONE idempotent row per trip). admin-svc
 *  only READS earnings-svc's driver_tips table through the shared pool — there is
 *  no create/edit/delete (tips are written by the app, never by an admin), so
 *  this page is list + detail only, exactly like the legacy Filament
 *  DriverTipResource (canCreate=false).
 *
 *  DEFENSIVE: shows a clean "not yet in Postgres" state when the tip ledger
 *  isn't in this DB yet (it's owned by earnings-svc; pending cutover). */
export function DriverTips() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<DriverTip | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'driver-tips', q, offset],
    queryFn: ({ signal }) => adminApi.driverTips({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<DriverTip>> = [
    {
      key: 'trip',
      header: 'Trip',
      cell: (r) => (
        <span className="metric truncate text-xs text-slate-300" title={r.trip_label}>
          {r.trip_label}
        </span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.customer_name}</div>
          <div className="metric text-[11px] text-muted">#{r.customer_id}</div>
        </div>
      ),
    },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Tip',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">+{aud(r.amount)}</span>,
    },
    {
      key: 'when',
      header: 'When',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver Tips"
        subtitle="Tips riders sent drivers after a trip (wallet → wallet). Read-only audit log; open one for detail."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by trip code / rider / driver…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver tips not yet in Postgres"
            description={
              data?.note ??
              'The tip ledger is owned by earnings-svc and is not in this Postgres yet. This page lights up automatically once it lands.'
            }
            icon={<IconReceipt className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <DriverTipStatsRow stats={data.stats} /> : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading driver tips…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load driver tips" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<DriverTip>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No tips"
                      description="No driver tips match this search."
                      icon={<IconReceipt className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <DriverTipDrawer tip={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The headline totals above the table: how many tips, gross tipped, average. */
function DriverTipStatsRow({ stats }: { stats: DriverTipStats }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      <StatTile label="Tips" value={stats.count.toLocaleString()} hint="all-time" />
      <StatTile label="Total tipped" value={aud(stats.total_amount)} hint="gross to drivers" />
      <StatTile label="Average tip" value={aud(stats.avg_amount)} hint="per tip" />
    </div>
  );
}

/** DriverTipDrawer — the full tip detail. The list row already carries every
 *  field, so no extra fetch is needed for the body. Read-only: a tip is an
 *  immutable ledger entry, so there are no actions here. */
function DriverTipDrawer({ tip: t, onClose }: { tip: DriverTip; onClose: () => void }) {
  return (
    <Drawer title={`Tip ${aud(t.amount)}`} subtitle={`Trip ${t.trip_label}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Field label="Amount (AUD)" value={<span className="text-ok">+{aud(t.amount)}</span>} />
          <Field label="Trip" value={<span className="metric">{t.trip_label}</span>} />
          <Field label="Customer" value={`${t.customer_name} (#${t.customer_id})`} />
          {t.customer_phone ? <Field label="Customer phone" value={<span className="metric">{t.customer_phone}</span>} /> : null}
          <Field label="Driver" value={`${t.driver_name} (#${t.driver_id})`} />
          {t.driver_phone ? <Field label="Driver phone" value={<span className="metric">{t.driver_phone}</span>} /> : null}
          <Field label="When" value={dateTime(t.created_at)} />
          <Field label="Ledger key" value={<span className="metric text-xs">{t.idempotency_key}</span>} />
        </div>

        <Card>
          <p className="text-xs text-muted">
            Tips are written by the app when a rider tips a driver — the rider's km-wallet is debited and the driver's AUD
            wallet credited in one idempotent ledger entry. This view is read-only; there is nothing to edit here.
          </p>
        </Card>
      </div>
    </Drawer>
  );
}
